import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  ClipboardList,
  Star,
  ArrowRight,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronRight,
  Flame,
  Calendar,
  TrendingUp,
} from "lucide-react";
import { MemberTier } from "../../app/api/authApi";
import { useAppSelector } from "../../app/hooks";
import { QuarterKey, useGetMyQuarterQuery } from "../../app/api/programmeApi";
import { useGetTasksQuery } from "../../app/api/tasksApi";
import { useGetJourneyStatsQuery } from "../../app/api/journeyApi";
import { useGetMyFellowsStatusQuery } from "../../app/api/fellowsApi";
import { useGetLibraryContentQuery } from "../../app/api/libraryApi";

/* ─────────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────────── */

const TIER_STYLES: Record<MemberTier, string> = {
  wider_audience: "bg-green-50  text-green-700  ring-green-200",
  inner_circle: "bg-blue-50   text-blue-700   ring-blue-200",
  legacy_fellows: "bg-purple-50 text-purple-700 ring-purple-200",
};

const TIER_LABELS: Record<MemberTier, string> = {
  wider_audience: "Wider Audience",
  inner_circle: "Inner Circle",
  legacy_fellows: "Legacy Fellows",
};

const CONTENT_CHIP: Record<string, string> = {
  keynote: "bg-green-100  text-green-700",
  panel: "bg-blue-100   text-blue-700",
  reading: "bg-purple-100 text-purple-700",
  resource: "bg-orange-100 text-orange-700",
  task: "bg-rose-100   text-rose-700",
};

/* ─────────────────────────────────────────────────────────────────
   PRIMITIVES
───────────────────────────────────────────────────────────────── */

const TierBadge: React.FC<{ tier: MemberTier }> = ({ tier }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${TIER_STYLES[tier]}`}
  >
    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
    {TIER_LABELS[tier]}
  </span>
);

const Bar: React.FC<{ pct: number; cls?: string }> = ({
  pct,
  cls = "bg-green-500",
}) => (
  <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
    <div
      className={`h-full rounded-full ${cls} transition-all duration-700`}
      style={{ width: `${Math.min(100, pct)}%` }}
    />
  </div>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h2 className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 mb-3">
    {children}
  </h2>
);

/* Loading skeleton */
const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

const PageSkeleton = () => (
  <div className="space-y-6 pb-12">
    <div className="flex items-start justify-between">
      <div className="space-y-2">
        <Bone className="h-3 w-28" />
        <Bone className="h-8 w-52" />
        <Bone className="h-4 w-40" />
      </div>
      <Bone className="h-7 w-28 rounded-full" />
    </div>
    <Bone className="h-44 w-full rounded-2xl" />
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[0, 1, 2, 3].map((i) => (
        <Bone key={i} className="h-20" />
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-3">
        <Bone className="h-3 w-20" />
        {[0, 1].map((i) => (
          <Bone key={i} className="h-24" />
        ))}
      </div>
      <div className="space-y-3">
        <Bone className="h-3 w-28" />
        {[0, 1, 2].map((i) => (
          <Bone key={i} className="h-16" />
        ))}
      </div>
    </div>
  </div>
);

const InlineError: React.FC<{ msg?: string }> = ({
  msg = "Could not load this section.",
}) => (
  <div className="flex items-center gap-2 text-xs text-red-500 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
    <AlertCircle size={13} className="shrink-0" />
    {msg}
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   Dashboard
───────────────────────────────────────────────────────────────── */

const Dashboard: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user)!;

  // Greeting based on time of day
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  /* ── Queries ── */
  // useGetMyQuarterQuery is the primary call — every mentee starts at Q1
  // and advances personally through Q2/Q3/Q4 regardless of the global cycle.
  const {
    data: myQuarterRes,
    isLoading: cycleLoading,
    isError: cycleError,
  } = useGetMyQuarterQuery();
  const cycle = myQuarterRes?.data?.cycle; // full cycle detail for the current quarter

  const {
    data: tasksRes,
    isLoading: tasksLoading,
    isError: tasksError,
  } = useGetTasksQuery(
    { quarter: cycle?.quarter as QuarterKey },
    { skip: !cycle?.quarter },
  );

  const { data: libraryRes, isLoading: libraryLoading } =
    useGetLibraryContentQuery(
      { quarter: cycle?.quarter as QuarterKey },
      { skip: !cycle?.quarter },
    );

  const { data: statsRes, isLoading: statsLoading } = useGetJourneyStatsQuery();
  const { data: fellowsRes } = useGetMyFellowsStatusQuery();

  const stats = statsRes?.data;
  const fellows = fellowsRes?.data;

  /* ── Derived ── */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allTasks = tasksRes?.data ?? [];

  const pendingTasks = useMemo(
    () =>
      allTasks
        .filter(
          (t) =>
            t.submission.status === "not_started" ||
            t.submission.status === "in_progress",
        )
        .slice(0, 2),
    [allTasks],
  );

  const taskDone = useMemo(
    () =>
      allTasks.filter(
        (t) =>
          t.submission.status === "submitted" ||
          t.submission.status === "graded",
      ).length,
    [allTasks],
  );
  const taskTotal = allTasks.length;

  const recentUploads = useMemo(
    () =>
      [...(libraryRes?.data ?? [])]
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )
        .slice(0, 3),
    [libraryRes],
  );

  const currentTier: MemberTier =
    (stats?.currentTier as MemberTier) ??
    (user.tier as MemberTier) ??
    "wider_audience";

  /* ── Primary loading / fatal error ── */
  if (cycleLoading || (statsLoading && !stats)) return <PageSkeleton />;

  if (cycleError)
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
          <AlertCircle size={20} className="text-red-400" />
        </div>
        <p className="text-sm font-semibold text-gray-700">
          Unable to load programme data
        </p>
        <p className="text-xs text-gray-400 max-w-xs">
          Check your connection and refresh the page.
        </p>
      </div>
    );

  /* ─────────────────────────────────────────────────────────────── */

  return (
    <div className="space-y-7 pb-12">
      {/* GREETING */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-green-600 mb-1">
            GIMLP Member Portal
          </p>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
            {greeting}, {user.firstName}.
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Here's what's happening in your programme this week.
          </p>
        </div>
        <TierBadge tier={currentTier} />
      </div>

      {/* QUARTER BANNER */}
      {cycle ? (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-700 to-green-950 p-6 text-white shadow-md">
          <div className="pointer-events-none absolute -right-10 -top-10 h-52 w-52 rounded-full border border-white/10" />
          <div className="pointer-events-none absolute -right-3 -top-3 h-32 w-32 rounded-full border border-white/10" />

          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="rounded-md bg-white/20 px-2 py-0.5 text-xs font-bold uppercase tracking-wide">
                  {cycle.quarter}
                </span>
                {cycle.tagline && (
                  <span className="text-white/60 text-xs">{cycle.tagline}</span>
                )}
              </div>
              <h2 className="text-lg font-semibold leading-snug">
                {cycle.theme}
              </h2>
              {cycle.pillars?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {cycle.pillars.map((p) => (
                    <span
                      key={p}
                      className="rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="sm:text-right shrink-0">
              <div className="flex sm:justify-end items-center gap-1.5 text-white/70 text-xs mb-1">
                <Calendar size={12} />
                <span>Next event</span>
              </div>
              <p className="text-5xl font-bold tabular-nums leading-none">
                {cycle.daysToEvent ?? "—"}
              </p>
              <p className="text-white/50 text-xs mt-1 mb-3">days away</p>
              <div className="w-full sm:w-36">
                <div className="flex justify-between text-[11px] text-white/50 mb-1.5">
                  <span>Quarter progress</span>
                  <span>{cycle.progressPercent}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-white transition-all duration-700"
                    style={{ width: `${cycle.progressPercent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <Bone className="h-44 w-full rounded-2xl" />
      )}

      {/* STATS ROW */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Quarter */}
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-4 shadow-sm">
          <p className="text-[11px] text-gray-400 mb-1">Quarter</p>
          <p className="text-xl font-bold text-green-700">
            {cycle?.quarter ?? "—"}
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5 truncate">
            {cycle?.tagline?.split(" ")[0] ?? "—"}
          </p>
        </div>

        {/* Tasks */}
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-4 shadow-sm">
          <p className="text-[11px] text-gray-400 mb-1">Tasks done</p>
          {tasksLoading ? (
            <Bone className="h-7 w-16 mt-1" />
          ) : (
            <>
              <p className="text-xl font-bold text-amber-600">
                {taskDone}/{taskTotal}
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5">
                {taskTotal - taskDone} remaining
              </p>
            </>
          )}
        </div>

        {/* Days */}
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-4 shadow-sm">
          <p className="text-[11px] text-gray-400 mb-1">Days to event</p>
          <p className="text-xl font-bold text-blue-600">
            {cycle?.daysToEvent ?? "—"}
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5">Day 90 execution</p>
        </div>

        {/* Tier */}
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-4 shadow-sm">
          <p className="text-[11px] text-gray-400 mb-1">Your tier</p>
          {statsLoading ? (
            <Bone className="h-7 w-20 mt-1" />
          ) : (
            <>
              <p className="text-xl font-bold text-purple-600">
                {TIER_LABELS[currentTier].split(" ")[0]}
              </p>
              {stats?.fellowsEligible && stats.fellowsEligibleQuarter && (
                <p className="text-[11px] text-purple-500 mt-0.5">
                  Fellows eligible · {stats.fellowsEligibleQuarter}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* TWO-COLUMN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* PENDING TASKS */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <SectionLabel>Pending tasks</SectionLabel>
            <Link
              to="/user/tasks"
              className="text-[11px] font-semibold text-green-600 hover:text-green-700 flex items-center gap-0.5 transition-colors"
            >
              All tasks <ChevronRight size={11} />
            </Link>
          </div>

          <div className="space-y-3">
            {tasksLoading ? (
              <>
                <Bone className="h-24" />
                <Bone className="h-24" />
              </>
            ) : tasksError ? (
              <InlineError msg="Could not load tasks." />
            ) : pendingTasks.length === 0 ? (
              <div className="flex items-center gap-2 px-4 py-3 bg-green-50 rounded-xl border border-green-100 text-sm text-green-700">
                <CheckCircle2 size={14} className="shrink-0" />
                <span>All tasks for this quarter are submitted!</span>
              </div>
            ) : (
              <>
                {pendingTasks.map((task) => {
                  const days = task.daysRemaining ?? 99;
                  const urgent = days <= 7;
                  const prog = task.submission.progressPercent;

                  return (
                    <Link
                      key={task.id}
                      to="/user/tasks"
                      className="block bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:border-green-200 hover:shadow-md transition-all duration-150 group"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2.5">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            {urgent ? (
                              <AlertCircle
                                size={12}
                                className="text-amber-500 shrink-0"
                              />
                            ) : (
                              <Clock
                                size={12}
                                className="text-gray-400 shrink-0"
                              />
                            )}
                            <h3 className="text-sm font-semibold text-gray-900 group-hover:text-green-700 transition-colors truncate">
                              Task {task.taskNumber} — {task.title}
                            </h3>
                          </div>
                          <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-2">
                            {task.description}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${urgent ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200" : "bg-gray-50 text-gray-500 ring-1 ring-gray-200"}`}
                        >
                          {days > 0 ? `${days}d left` : "Due today"}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <Bar
                          pct={prog}
                          cls={prog === 0 ? "bg-gray-300" : "bg-green-500"}
                        />
                        <p className="text-[10px] text-gray-400">
                          {prog === 0 ? "Not started" : `${prog}% complete`}
                        </p>
                      </div>
                    </Link>
                  );
                })}

                <div className="flex items-center gap-2 px-4 py-3 bg-green-50 rounded-xl border border-green-100 text-sm text-green-700">
                  <CheckCircle2 size={14} className="shrink-0" />
                  <span>
                    <strong>{taskDone}</strong> of {taskTotal} tasks completed
                    this quarter
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* RECENT UPLOADS */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <SectionLabel>Recent library uploads</SectionLabel>
            <Link
              to="/user/library"
              className="text-[11px] font-semibold text-green-600 hover:text-green-700 flex items-center gap-0.5 transition-colors"
            >
              Full library <ChevronRight size={11} />
            </Link>
          </div>

          <div className="space-y-3">
            {libraryLoading ? (
              <>
                <Bone className="h-16" />
                <Bone className="h-16" />
                <Bone className="h-16" />
              </>
            ) : recentUploads.length === 0 ? (
              <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded-xl border border-gray-100 px-4 py-4">
                <BookOpen size={13} className="shrink-0" />
                No content uploaded yet for this quarter.
              </div>
            ) : (
              recentUploads.map((item) => (
                <Link
                  key={item.id}
                  to="/user/library"
                  className="flex items-start gap-3 bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:border-green-200 hover:shadow-md transition-all duration-150 group"
                >
                  <div
                    className={`shrink-0 rounded-lg w-10 h-10 flex items-center justify-center text-[9px] font-bold uppercase tracking-wide ${CONTENT_CHIP[item.contentType] ?? "bg-gray-100 text-gray-500"}`}
                  >
                    {item.contentType.slice(0, 3)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <h3 className="text-sm font-semibold text-gray-900 group-hover:text-green-700 transition-colors truncate">
                        {item.title}
                      </h3>
                      {item.isNew && (
                        <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500 text-white uppercase tracking-wide">
                          New
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-2">
                      {item.description}
                    </p>
                    {item.durationLabel && (
                      <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                        <Clock size={9} />
                        {item.durationLabel}
                      </p>
                    )}
                  </div>
                  <ArrowRight
                    size={13}
                    className="shrink-0 text-gray-300 group-hover:text-green-500 mt-0.5 transition-colors"
                  />
                </Link>
              ))
            )}
          </div>
        </div>
      </div>

      {/* QUICK LINKS */}
      <div>
        <SectionLabel>Explore</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            {
              label: "Library",
              Icon: BookOpen,
              desc: "Recordings, reading lists & resources",
              path: "/user/library",
              bg: "bg-green-50",
              ic: "text-green-600",
              ring: "hover:ring-green-200",
            },
            {
              label: "Tasks",
              Icon: ClipboardList,
              desc: tasksLoading
                ? "Loading…"
                : `${taskTotal - taskDone} remaining`,
              path: "/user/tasks",
              bg: "bg-amber-50",
              ic: "text-amber-600",
              ring: "hover:ring-amber-200",
            },
            {
              label: "Fellows",
              Icon: Star,
              desc: "Learn about the Legacy Fellows program",
              path: "/user/fellows",
              bg: "bg-purple-50",
              ic: "text-purple-600",
              ring: "hover:ring-purple-200",
            },
          ].map(({ label, Icon, desc, path, bg, ic, ring }) => (
            <Link
              key={label}
              to={path}
              className={`group flex items-center gap-4 bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-4 hover:shadow-md hover:ring-1 ${ring} transition-all duration-150`}
            >
              <div
                className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center shrink-0`}
              >
                <Icon size={17} className={ic} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                  {label}
                </p>
                <p className="text-[11px] text-gray-400 truncate">{desc}</p>
              </div>
              <ChevronRight
                size={13}
                className="text-gray-300 group-hover:text-green-500 transition-colors shrink-0"
              />
            </Link>
          ))}
        </div>
      </div>

      {/* PROGRAMME PROGRESS CARD */}
      {stats && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">
              Your programme progress
            </h3>
            <Link
              to="/user/journey"
              className="text-[11px] font-semibold text-green-600 hover:text-green-700 flex items-center gap-0.5 transition-colors"
            >
              Full journey <ChevronRight size={11} />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              {
                label: "Quarters done",
                value: stats.quartersCompleted,
                colour: "text-green-700",
                icon: <TrendingUp size={12} className="text-green-500" />,
              },
              {
                label: "Tasks submitted",
                value: stats.totalTasksSubmitted,
                colour: "text-amber-600",
                icon: <ClipboardList size={12} className="text-amber-500" />,
              },
              {
                label: "Content viewed",
                value: stats.totalContentViewed,
                colour: "text-blue-600",
                icon: <BookOpen size={12} className="text-blue-500" />,
              },
              {
                label: "Avg. score",
                value:
                  stats.overallAvgScore !== null
                    ? `${stats.overallAvgScore.toFixed(1)}/10`
                    : "—",
                colour: "text-purple-600",
                icon: <Star size={12} className="text-purple-400" />,
              },
            ].map((s) => (
              <div key={s.label} className="space-y-1">
                <div className="flex items-center gap-1.5 text-[10px] text-gray-400 font-medium">
                  {s.icon}
                  {s.label}
                </div>
                <p className={`text-xl font-bold ${s.colour}`}>{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FELLOWS NUDGE — only shown when eligible */}
      {fellows?.eligible && (
        <div className="relative overflow-hidden rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 to-white p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-purple-100 shrink-0">
            <Flame size={20} className="text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 mb-0.5">
              You may be eligible for Legacy Fellows
              {fellows.eligibleQuarter ? ` — ${fellows.eligibleQuarter}` : ""}
            </h3>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              Complete your remaining tasks and engage with this quarter's
              library content to strengthen your nomination. Only 10 Fellows are
              selected per quarter.
            </p>
          </div>
          <Link
            to="/user/fellows"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-xs font-semibold text-white hover:bg-purple-700 active:scale-95 transition-all"
          >
            Learn more <ArrowRight size={12} />
          </Link>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
